import React, { useEffect, useState } from "react";
import Table, { Row, Data } from "../../components/ui/table/table";

import { fetchCardLoadReport } from "../../api/card-service";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { getFormattedDate, getAmountWithCurrency } from "../../utils/utils";
import PageWrapper from "../../layouts/PageWrapper";
import Section from "../../layouts/Section";

import { Input, Select } from "../../components/form/formControls";
import { StatusBadge } from "../../components/StatusBadge";

import SubUserSelector from "./SubUserSelector";

import { useSelector } from "react-redux";
import { useMemo } from "react";
import { useDebounce } from "../../hooks/useDebounce";
import { downloadReportingCardLoad } from "../../utils/downloadReport";
import { Button } from "../../components/ui/buttons/Button";
import Pagination from "../../components/Pagination";
import { usePagination } from "../../hooks/usePagination";
import { useLocale } from "../../hooks/useLocale";

const ReportsCardLoad = () => {
  const queryClient = useQueryClient();

  // globals
  const inputDate = useSelector((s) => s.global.inputDate);
  const currentUser = useSelector((s) => s.auth.currentUser);

  // user selected from the sub user list
  const selectedUser = useSelector((s) => s.reports.selectedUser);

  // Current Selcted Item
  const [isDownoadingReport, setIsDownoadingReport] = useState(false);

  const { localize } = useLocale(["reports"]);

  // Handling Filters
  const filterSchema = {
    status: "",
    moxeyId: "",
    drivernameEn: "",
    orderId: "",
    cashReceiptVoucher: "",
    senderName: "",
    referenceNo: "",
  };
  const [filters, setFilters] = useState(filterSchema);
  const handleFilter = (filteredValues) => setFilters(filteredValues);
  const debouncedHandleFilters = useDebounce(handleFilter);
  const { page } = usePagination();

  const reportQuery = useQuery(
    ["reports/cardload", filters, inputDate, page, selectedUser?.userHashId],
    () =>
      fetchCardLoadReport({
        userHashId: selectedUser?.userHashId,
        userType: selectedUser?.productType,
        dateRange: inputDate.date,
        filters,
        page,
      }),
    {
      enabled: !!selectedUser,
    }
  );

  // download report
  const handleDownloadReport = () => {
    setIsDownoadingReport(true);
    downloadReportingCardLoad({
      dateRange: inputDate.date,
      userHashId: selectedUser?.userHashId,
      email: selectedUser.emailId,
      username: selectedUser.name,
    })
      .then((res) => {
        setIsDownoadingReport(false);
      })
      .catch((err) => {});
  };

  useEffect(() => {
    return () => {
      queryClient.cancelQueries(["cards/list"]);
    };
  }, [queryClient]);

  return (
    <>
      <PageWrapper>
        {/* Page Header */}
        <Section>
          <div className="d-flex">
            <h3>{localize("heading")}</h3>
            <div className="ms-auto">
              <SubUserSelector />
            </div>
          </div>
        </Section>

        {/* Table Data */}
        <Section>
          <>
            <div className="d-flex justify-content-between align-items-center mt-4">
              <h5 className="text-muted">{localize("cardLoad.heading")}</h5>

              <Button
                text={localize("cardLoad.actions.downloadReport")}
                loading={isDownoadingReport}
                className="btn btn-primary"
                onClick={handleDownloadReport}
              />
            </div>

            {/* Table Data */}
            <Table
              loading={reportQuery.isLoading}
              cols={localize("cardLoad.table.cols", { returnObjects: true })}
              dataSize={reportQuery?.data?.content.length}
              filters={
                <TableFilters
                  filterSchema={filters}
                  handleFilter={debouncedHandleFilters}
                />
              }
            >
              {/* =============== Display Data from API =============*/}
              {reportQuery?.data?.content.map((item) => (
                <>
                  <Row>
                    {/* Status */}
                    <Data>
                      <StatusBadge status={item.status} />
                    </Data>

                    <Data>{item.moxeyId ? item.moxeyId : "-"}</Data>
                    <Data>
                      {item.firstName} {item.lastName}
                    </Data>
                    <Data>
                      {item.toUserRole === "Broker User" ? "PARTNER" : "DRIVER"}
                    </Data>
                    <Data>{item.orderId}</Data>
                    <Data>{item.cashReceiptVoucher}</Data>
                    <Data>{item.senderName}</Data>
                    <Data>
                      {getAmountWithCurrency(item.amount, item.currency)}
                    </Data>
                    <Data>{item.referenceNo}</Data>
                    <Data>{getFormattedDate(item.createdAt)}</Data>
                  </Row>
                </>
              ))}
              {/* show empty list */}
            </Table>

            {/* ======= Pagination =======*/}
            <div className="d-flex justify-content-end mb-5">
              <Pagination
                show={reportQuery.data?.content.length}
                totalPages={reportQuery.data?.totalPages}
                handlePageSelect={(page) =>
                  setFilters({ ...filters, page: page })
                }
              />
            </div>
          </>
        </Section>
      </PageWrapper>
    </>
  );
};

const TableFilters = ({ filterSchema, handleFilter }) => {
  {
    /**
     *
     * Note : "name" attribute is important for filter controls
     * Interpreted as : [name] and filterSchema.name
     *
     */
  }
  const statusOptions = [
    {
      value: "All",
      text: "All",
    },
    {
      value: "Requested",
      text: "Requested",
    },
    {
      value: "Rejected",
      text: "Rejected",
    },
    {
      value: "Approved",
      text: "Approved",
    },
    {
      value: "Pending",
      text: "Pending",
    },
    {
      value: "InProcess",
      text: "InProcess",
    },
  ];

  // table filter has its own state for persisting the filter values
  const [filteredValues, setFilteredValues] = useState(filterSchema);

  const handleFilteredValues = (event) => {
    const { name, value } = event.target;
    setFilteredValues({ ...filteredValues, [name]: value });
    handleFilter({ ...filteredValues, [name]: value, page: 0 });
  };

  return (
    <>
      <tr>
        <td>
          <Select
            name="status"
            value={filteredValues.status}
            onChange={(event) => handleFilteredValues(event)}
          >
            <option value="" selected>
              Select status
            </option>
            {statusOptions.map((option) => (
              <option value={option.value}>{option.text}</option>
            ))}
          </Select>
        </td>
        <td>
          <Input
            name="moxeyId"
            value={filteredValues.moxeyId}
            onInput={(event) => handleFilteredValues(event)}
          />
        </td>
        <td>
          <Input
            name="drivernameEn"
            value={filteredValues.drivernameEn}
            onInput={(event) => handleFilteredValues(event)}
          />
        </td>
        <td></td>
        <td>
          <Input
            name="orderId"
            value={filteredValues.orderId}
            onInput={(event) => handleFilteredValues(event)}
          />
        </td>
        <td>
          <Input
            name="cashReceiptVoucher"
            value={filteredValues.cashReceiptVoucher}
            onInput={(event) => handleFilteredValues(event)}
          />
        </td>
        <td>
          <Input
            name="senderName"
            value={filteredValues.senderName}
            onInput={(event) => handleFilteredValues(event)}
          />
        </td>
        <td></td>
        <td>
          <Input
            name="referenceNo"
            value={filteredValues.referenceNo}
            onInput={(event) => handleFilteredValues(event)}
          />
        </td>
        <td></td>
      </tr>
    </>
  );
};

export default ReportsCardLoad;
